import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clearSession } from '../auth/session';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';
import LanguageSwitcher from './LanguageSwitcher';
import { SubscriptionApi, type SubscriptionDto } from '../api/SubscriptionApi';

interface HeaderProps {
  showNavigation?: boolean;
}

const Header: React.FC<HeaderProps> = ({ showNavigation = true }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [subscription, setSubscription] = useState<SubscriptionDto | null>(null);
  const [showExtendModal, setShowExtendModal] = useState(false);

  const isTransport = localStorage.getItem('role') === 'ROLE_TRANSPORT';

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      const res = await SubscriptionApi.getMySubscription();
      if (res.status === 204 || !res.data) {
        setSubscription(null);
      } else {
        setSubscription(res.data);
      }
    } catch (err: any) {
      console.error('Failed to load subscription:', err);
      setSubscription(null);
    }
  };

  const getSubscriptionColor = () => {
    if (!subscription) return '#999';
    if (subscription.infiniteSubscription) return '#4caf50';
    if (subscription.remainingDays > 7) return '#4caf50';
    if (subscription.remainingDays >= 3) return '#ffc107';
    return '#f44336';
  };

  const handleLogout = () => {
    clearSession();
    navigate('/login');
  };

  return (
    <header className="app-header" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      height: 70,
      background: 'linear-gradient(135deg, #1559b0 0%, #3abbeb 100%)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 24px',
      zIndex: 1000
    }}>
      {/* Logo and Title */}
      <Link to="/" className="app-header-brand" aria-label={t('chat.home')} style={{
        textDecoration: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 16
      }}>
        <Logo width={50} height={50} />
        <div>
          <h1 style={{
            margin: 0,
            color: 'white',
            fontSize: 24,
            fontWeight: 700,
            textShadow: '0 1px 2px rgba(0,0,0,0.2)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8
          }}>
            <span>{t('app.title') || 'StockFlow'}</span>
            <span style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 0.8,
              color: '#0d47a1',
              background: '#fff3cd',
              border: '1px solid #ffe082',
              borderRadius: 999,
              padding: '2px 8px',
              lineHeight: 1.2,
              textShadow: 'none'
            }}>
              BETA
            </span>
          </h1>
          <p className="app-header-subtitle" style={{
            margin: 0,
            color: 'rgba(255,255,255,0.8)',
            fontSize: 14,
            fontWeight: 400
          }}>
            {t('app.subtitle') || 'Management System'}
          </p>
        </div>
      </Link>

      {/* Right side controls */}
      {showNavigation && (
        <div className="app-header-actions" style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16
        }}>
          {/* Subscription Badge */}
          {subscription && !isTransport && (
            <button
              type="button"
              className="subscription-badge"
              onClick={() => setShowExtendModal(true)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                borderRadius: 8,
                padding: '8px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                color: 'inherit',
                transition: 'all 0.2s ease',
                border: `2px solid ${getSubscriptionColor()}`
              }}
              onMouseEnter={(e) => {
                if (!subscription.infiniteSubscription) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
              }}
              title={t(subscription.infiniteSubscription ? 'subscription.manageUnlimited' : 'subscription.extendHint')}
            >
              <div style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: getSubscriptionColor(),
                boxShadow: `0 0 8px ${getSubscriptionColor()}`
              }} />
              <span style={{ color: 'white', fontWeight: 600, fontSize: 14 }}>
                {subscription.infiniteSubscription ? (
                  <>∞ {subscription.plan}</>
                ) : (
                  <>{subscription.remainingDays}d {subscription.plan}</>
                )}
              </span>
            </button>
          )}
          
          <LanguageSwitcher variant="header" />
          <button
            onClick={handleLogout}
            title={t('navigation.logout')}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: 12,
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 16,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              backdropFilter: 'blur(10px)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
            }}
            aria-label={t('navigation.logout')}
          >
            <svg 
              style={{ fontSize: 20 }} 
              xmlns="http://www.w3.org/2000/svg" 
              width="1em" 
              height="1em" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      )}

      {/* Subscription Modal */}
      {showExtendModal && subscription && !isTransport && (
        <div
          className="subscription-modal-backdrop"
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowExtendModal(false);
            }
          }}
          role="presentation"
        >
          <div className="subscription-modal" role="dialog" aria-modal="true" aria-labelledby="subscription-dialog-title" style={{
            background: '#fff', borderRadius: 16, padding: 28,
            width: '92%', maxWidth: 640,
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
          }}>
            {/* Header row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 id="subscription-dialog-title" style={{ margin: 0, color: '#1976d2', fontSize: 20 }}>💳 {t('subscription.title')}</h3>
              <button
                onClick={() => setShowExtendModal(false)}
                aria-label={t('common.close')}
                style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#999', lineHeight: 1 }}
              >✕</button>
            </div>

            {/* Current status bar */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 20,
              background: '#f5f5f5', borderRadius: 10, padding: '12px 16px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{t('subscription.plan')}</div>
                <div style={{ fontWeight: 700, color: '#333' }}>{subscription.plan}</div>
              </div>
              <div style={{ textAlign: 'center', borderLeft: '1px solid #ddd', borderRight: '1px solid #ddd' }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{t('subscription.status')}</div>
                <div style={{ fontWeight: 700, color: subscription.infiniteSubscription ? '#9c27b0' : subscription.status === 'ACTIVE' ? '#4caf50' : '#f44336' }}>
                  {subscription.infiniteSubscription ? `∞ ${t('subscription.infinite')}` : t(`subscription.statuses.${subscription.status}`, { defaultValue: subscription.status })}
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{t('subscription.daysLeft')}</div>
                <div style={{ fontWeight: 700, color: subscription.infiniteSubscription ? '#4caf50' : subscription.remainingDays < 7 ? '#f44336' : '#4caf50' }}>
                  {subscription.infiniteSubscription ? '∞' : subscription.remainingDays}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 18, padding: 22, borderRadius: 14, background: 'linear-gradient(135deg,#eef4ff,#f8fbff)', border: '1px solid #b2ccff', textAlign: 'center' }}>
              <div style={{ color: '#175cd3', fontSize: 14, fontWeight: 700 }}>{t('subscription.monthlyPlan')}</div>
              <div style={{ marginTop: 6, color: '#101828', fontSize: 34, fontWeight: 800 }}>€50</div>
              <div style={{ color: '#667085', fontSize: 14 }}>{t('subscription.perMonth')}</div>
            </div>

            <div style={{ padding: 16, marginBottom: 18, borderRadius: 10, background: '#fff8e7', border: '1px solid #fedf89', color: '#7a2e0e', lineHeight: 1.55, fontSize: 14 }}>
              <strong>{t('subscription.paymentUnavailableTitle')}</strong>
              <div style={{ marginTop: 5 }}>{t('subscription.paymentUnavailableDescription')}</div>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setShowExtendModal(false)}
                style={{
                  flex: 1, background: '#1976d2', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '12px 16px', fontWeight: 600,
                  cursor: 'pointer', fontSize: 15
                }}
              >
                {t('common.close')}
              </button>
            </div>

            <p style={{ fontSize: 12, color: '#bbb', marginTop: 14, marginBottom: 0, textAlign: 'center', fontStyle: 'italic' }}>
              {t('subscription.contactAdministrator')}
            </p>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
